/**
 * OpenSeadragon tile source for Morphle pyramids.
 *
 * Morphle stitched levels are numbered 0 (coarsest) → maxMorphleLevel (full resolution), and the
 * stitched tile URL uses that number as `{z}`, the same as the OpenLayers zoom index.
 * OSD numbers levels 0 → ceil(log2(max(w, h))) at full resolution, so:
 *   osdLevel = morphleLevel + (osdNaturalMaxLevel - maxMorphleLevel)
 */

import {getPrefixedUrlSecure, getSuffixUrl} from '../../../../utils/utils';
import {getImageInfo, getViewLevelsInfo, getZoomLevels} from '../../utils/map_utils';
import {manifestTileExists} from './stitchManifest';

const DEFAULT_STITCHED_TILE_SIZE = 496;

export const getImageDimensions = (slide) => {
    if (!slide) return {width: 0, height: 0};
    if (slide.specimen_type === 'preview') {
        return {
            width: slide.x_step * slide.x_fields,
            height: slide.y_step * slide.y_fields,
        };
    }
    if (!slide.stitched_x_max || !slide.stitched_tile_width) {
        return {
            width: slide.tile_width * slide.x_fields,
            height: slide.tile_height * slide.y_fields,
        };
    }
    return {
        width: slide.stitched_tile_width * slide.stitched_x_max,
        height: slide.stitched_tile_height * slide.stitched_y_max,
    };
};

export const getStitchedTileSize = (slide) => ({
    width: slide.stitched_tile_width || DEFAULT_STITCHED_TILE_SIZE,
    height: slide.stitched_tile_height || DEFAULT_STITCHED_TILE_SIZE,
});

/** Levels are zoom indices into `z_levels`, which is what OL puts in the stitched `{z}`. */
export const getZoomInfo = (slide) => {
    const zoomLevels = getZoomLevels(slide);
    return {
        zoomLevels,
        minMorphleLevel: 0,
        maxMorphleLevel: Math.max(0, zoomLevels.length - 1),
        ...getViewLevelsInfo(slide),
    };
};

const getLevelOffset = (slide) => {
    const {width, height} = getImageDimensions(slide);
    const {zoomLevels, maxMorphleLevel} = getZoomInfo(slide);
    if (!zoomLevels.length || !width || !height) return null;
    return Math.ceil(Math.log2(Math.max(width, height))) - maxMorphleLevel;
};

export const morphleToOsdLevel = (slide, morphleLevel) => {
    const offset = getLevelOffset(slide);
    return offset == null ? null : morphleLevel + offset;
};

export const getBackgroundColor = (slide) => {
    const bgColor = slide.background_color?.split(',').map(Number) || [220, 220, 230];
    return `rgb(${bgColor[0]}, ${bgColor[1]}, ${bgColor[2]})`;
};

/**
 * Tiled camera fields are drawn above the stitched pyramid by SlidemapOSD (same rule as OpenLayers
 * `isZoomForTiled`). Returns null when the slide has no tiled levels to overlay.
 */
export const getTiledOverlayConfig = (slide) => {
    if (!slide?.txty || Object.keys(slide.txty).length === 0) return null;
    const imageInfo = getImageInfo(slide);
    if (!imageInfo?.imageSizes) return null;
    const {lastStitchedLevel, numberOfTiledLevels, lastTiledLevel, smallestTiledLevel} = getViewLevelsInfo(slide);
    if (smallestTiledLevel === -1 || lastStitchedLevel < 0 || !(numberOfTiledLevels > 0)) return null;
    return {
        useTiledAboveLevel: lastStitchedLevel - numberOfTiledLevels,
        tiledBaseUrl: getPrefixedUrlSecure(getSuffixUrl(slide, true, 0), slide),
        imageInfo,
        lastStitchedLevel,
        lastTiledLevel,
        smallestTiledLevel,
    };
};

/** Tiled zoom for a view level, as OpenLayers computes it in Slidemap.updateLayers. */
export const getTiledZoom = (tiledConfig, viewLevel) => {
    const {lastStitchedLevel, lastTiledLevel, smallestTiledLevel, imageInfo} = tiledConfig;
    let tiledZoom = Math.min(viewLevel - (lastStitchedLevel - lastTiledLevel), lastTiledLevel);
    tiledZoom = Math.max(smallestTiledLevel, tiledZoom);
    if (tiledZoom < 0 || tiledZoom >= imageInfo.imageSizes.length) return null;
    return tiledZoom;
};

/**
 * OSD only requests levels that map to stitched Morphle levels. Levels served by the tiled overlay
 * return no URL, so OSD keeps drawing the finest stitched level underneath. With a stitch manifest,
 * tiles missing on disk are not requested either.
 *
 * `txty` and `stitchManifest` usually reach the slide after the viewer opens, so they are read from
 * `getCurrentSlide()` on every tile lookup; dimensions and levels come from the slide at creation.
 * OSD remembers each tile's URL, so callers must clear `tiledImage.tilesMatrix` when either changes.
 *
 * Levels above `getLevelCap()` are not fetched; OSD upscales the level below (virtual digital zoom).
 */
export const createTileSourceFromSlide = (slide, getCurrentSlide = () => slide, getLevelCap = () => Infinity) => {
    const {width, height} = getImageDimensions(slide);
    const tileSize = getStitchedTileSize(slide);
    const {minMorphleLevel, maxMorphleLevel} = getZoomInfo(slide);
    const levelOffset = getLevelOffset(slide) ?? 0;
    const stitchedUrlTemplate = getPrefixedUrlSecure(getSuffixUrl(slide, false, 0), slide);

    let liveFor = null;
    let live = {useTiledAboveLevel: null, manifest: null};
    const getLive = () => {
        const current = getCurrentSlide();
        const source = current?.id === slide.id ? current : slide;
        if (source !== liveFor) {
            liveFor = source;
            const manifest = source.stitchManifest;
            live = {
                useTiledAboveLevel: getTiledOverlayConfig(source)?.useTiledAboveLevel ?? null,
                manifest: manifest && manifest.zMax === maxMorphleLevel ? manifest : null,
            };
        }
        return live;
    };

    const isServedTile = (morphleLevel, x, y) => {
        if (morphleLevel < minMorphleLevel || morphleLevel > Math.min(maxMorphleLevel, getLevelCap())) return false;
        const {useTiledAboveLevel, manifest} = getLive();
        if (useTiledAboveLevel != null && morphleLevel > useTiledAboveLevel) return false;
        if (!manifest?.levels[morphleLevel]) return true;
        return manifestTileExists(manifest, morphleLevel, x, y);
    };

    return {
        width,
        height,
        tileSize: tileSize.width,
        tileOverlap: 0,
        minLevel: Math.max(0, levelOffset + minMorphleLevel),
        maxLevel: levelOffset + maxMorphleLevel,

        getTileUrl: (level, x, y) => {
            const morphleLevel = level - levelOffset;
            if (!isServedTile(morphleLevel, x, y)) return null;
            return stitchedUrlTemplate
                .replace(/\{z\}/g, String(morphleLevel))
                .replace(/\{x\}/g, String(x))
                .replace(/\{y\}/g, String(y));
        },

        tileExists: (level, x, y) => isServedTile(level - levelOffset, x, y),
    };
};
